import { useState, useEffect } from 'react';
import { View, Text, ScrollView, Alert, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { useCommentStore } from '../../store/commentStore';
import { authApi } from '../../shared/utils/api';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName, getInitials } from '../../shared/utils/user';
import { Button, Input } from '../../components/ui';
import { Card, SectionCard } from '../../components/ui';
import { PageContainer, PageHeader } from '../../components/layout';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';

export default function SettingsPage() {
  const router = useRouter();
  const { user, refreshUser, clearAuth } = useAuthStore();
  const clearCommentCache = useCommentStore((state) => state.clearAllCache);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();

  // Profile state
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.display_name) {
      setDisplayName(user.display_name);
    }
  }, [user?.display_name]);

  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      Alert.alert('Error', 'Display name is required');
      return;
    }

    if (displayName.trim().length < 2) {
      Alert.alert('Error', 'Display name must be at least 2 characters');
      return;
    }

    setSavingProfile(true);
    try {
      await authApi.updateProfile({ display_name: displayName.trim() });
      await refreshUser();
      // Clear comment cache so refreshed data shows new display name
      clearCommentCache();
      setIsEditingProfile(false);
      Alert.alert('Success', 'Profile updated successfully!');
    } catch (error: any) {
      logger.error('Error saving profile', error, 'settings');
      const message = error?.message || error?.data?.message || 'Failed to update profile';
      Alert.alert('Error', message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      Alert.alert('Error', 'Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert('Error', 'New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      Alert.alert('Error', 'Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await authApi.changePassword({ 
        currentPassword, 
        newPassword 
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      Alert.alert('Success', 'Password changed successfully!');
    } catch (error: any) {
      logger.error('Error changing password', error, 'settings');
      const message = error?.message || error?.data?.message || 'Failed to change password';
      Alert.alert('Error', message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to log out?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Logout', 
          style: 'destructive',
          onPress: async () => {
            await clearAuth();
            router.replace('/(auth)/login');
          }
        },
      ]
    );
  };

  const initials = getInitials(user);
  const currentDisplayName = getDisplayName(user);

  return (
    <PageContainer>
      <PageHeader 
        title="Settings" 
      />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
          paddingBottom: utils.spacing.xl * 2,
        }}
        showsVerticalScrollIndicator={true}
      >
        {/* Profile Section */}
        <SectionCard 
          title="Profile" 
          style={{ marginBottom: utils.spacing.lg }}
        >
          <View style={{ 
            alignItems: 'center', 
            marginBottom: utils.spacing.lg,
            paddingVertical: utils.spacing.md,
          }}>
            {/* Avatar */}
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: tokens.color.primary,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.md,
            }}>
              <Text style={{
                fontSize: 28,
                fontWeight: '700',
                color: tokens.color.onPrimary,
              }}>
                {initials}
              </Text>
            </View>

            {/* Display Name */}
            <Text style={{
              fontSize: utils.typography.sizes.xl,
              fontWeight: utils.typography.weights.bold,
              color: tokens.color.onSurface,
              marginBottom: utils.spacing.xs,
            }}>
              {currentDisplayName}
            </Text>

            {/* Email (for reference only) */}
            <Text style={{
              fontSize: utils.typography.sizes.sm,
              color: addColorOpacity(tokens.color.onSurface, 0.6),
            }}>
              {user?.email}
            </Text>
          </View>

          {isEditingProfile ? (
            <View style={{ gap: utils.spacing.md }}>
              <Input
                label="Display Name"
                value={displayName}
                onChangeText={setDisplayName}
                placeholder="Enter your display name"
                hint="This is how other users will see you"
                autoCapitalize="words"
                editable={!savingProfile}
              />

              <View style={{ 
                flexDirection: 'row', 
                gap: utils.spacing.sm,
                marginTop: utils.spacing.sm,
              }}>
                <Button
                  title="Cancel"
                  onPress={() => {
                    setDisplayName(user?.display_name || '');
                    setIsEditingProfile(false);
                  }}
                  variant="outline"
                  style={{ flex: 1 }}
                  disabled={savingProfile}
                />
                <Button
                  title={savingProfile ? 'Saving...' : 'Save'}
                  onPress={handleSaveProfile}
                  style={{ flex: 1 }}
                  disabled={savingProfile || !displayName.trim()}
                />
              </View>
            </View>
          ) : (
            <Button
              title="Edit Profile"
              onPress={() => setIsEditingProfile(true)}
              variant="outline"
            />
          )}
        </SectionCard>

        {/* Account Section */}
        <SectionCard 
          title="Account" 
          style={{ marginBottom: utils.spacing.lg }}
        >
          <View style={{ gap: utils.spacing.md }}>
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: utils.spacing.sm,
              borderBottomWidth: 1,
              borderBottomColor: tokens.color.border,
            }}>
              <View>
                <Text style={{
                  fontSize: utils.typography.sizes.sm,
                  color: addColorOpacity(tokens.color.onSurface, 0.6),
                  marginBottom: 2,
                }}>
                  Email
                </Text>
                <Text style={{
                  fontSize: utils.typography.sizes.base,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.medium,
                }}>
                  {user?.email}
                </Text>
              </View>
              <Ionicons 
                name="lock-closed" 
                size={18} 
                color={addColorOpacity(tokens.color.onSurface, 0.4)} 
              />
            </View>

            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: utils.spacing.sm,
            }}>
              <View>
                <Text style={{
                  fontSize: utils.typography.sizes.sm,
                  color: addColorOpacity(tokens.color.onSurface, 0.6),
                  marginBottom: 2,
                }}>
                  Role
                </Text>
                <Text style={{
                  fontSize: utils.typography.sizes.base,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.medium,
                  textTransform: 'capitalize',
                }}>
                  {user?.role || 'User'}
                </Text>
              </View>
              <Ionicons 
                name={user?.role === 'admin' ? 'shield-checkmark' : 'person'} 
                size={18} 
                color={user?.role === 'admin' ? tokens.color.primary : addColorOpacity(tokens.color.onSurface, 0.4)} 
              />
            </View>
          </View>
        </SectionCard>

        {/* Security Section */}
        <SectionCard 
          title="Security" 
          style={{ marginBottom: utils.spacing.lg }}
        >
          <View style={{ gap: utils.spacing.md }}>
            <Input
              label="Current Password"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              placeholder="Enter current password"
              secureTextEntry
              autoCapitalize="none"
              editable={!changingPassword}
            />

            <Input
              label="New Password"
              value={newPassword}
              onChangeText={setNewPassword}
              placeholder="Enter new password"
              secureTextEntry
              autoCapitalize="none"
              hint="At least 8 characters with uppercase, lowercase, and numbers"
              editable={!changingPassword}
            />

            <Input
              label="Confirm New Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Confirm new password"
              secureTextEntry
              autoCapitalize="none"
              editable={!changingPassword}
            />

            <Button
              title={changingPassword ? 'Changing Password...' : 'Change Password'}
              onPress={handleChangePassword}
              variant="outline"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              style={{ marginTop: utils.spacing.sm }}
            />
          </View>
        </SectionCard>

        {/* About Section */}
        <SectionCard 
          title="About" 
          style={{ marginBottom: utils.spacing.lg }}
        >
          <View style={{ gap: utils.spacing.md }}>
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: utils.spacing.sm,
              borderBottomWidth: 1,
              borderBottomColor: tokens.color.border,
            }}>
              <View>
                <Text style={{
                  fontSize: utils.typography.sizes.sm,
                  color: addColorOpacity(tokens.color.onSurface, 0.6),
                  marginBottom: 2,
                }}>
                  Version
                </Text>
                <Text style={{
                  fontSize: utils.typography.sizes.base,
                  color: tokens.color.onSurface,
                  fontWeight: utils.typography.weights.medium,
                }}>
                  1.0.0
                </Text>
              </View>
              <Ionicons 
                name="information-circle-outline" 
                size={18} 
                color={addColorOpacity(tokens.color.onSurface, 0.4)} 
              />
            </View>

            <View style={{
              paddingVertical: utils.spacing.sm,
              borderBottomWidth: 1,
              borderBottomColor: tokens.color.border,
            }}>
              <Text style={{
                fontSize: utils.typography.sizes.sm,
                color: addColorOpacity(tokens.color.onSurface, 0.6),
                marginBottom: utils.spacing.xs,
              }}>
                About Farmer John
              </Text>
              <Text style={{
                fontSize: utils.typography.sizes.sm,
                color: tokens.color.onSurface,
                lineHeight: 20,
              }}>
                Farmer John is a partnership-first platform for managing real estate investments, 
                particularly farm and rural properties. Built for partners who research, acquire, 
                and manage properties together.
              </Text>
            </View>

            <View style={{
              paddingVertical: utils.spacing.sm,
            }}>
              <Text style={{
                fontSize: utils.typography.sizes.sm,
                color: addColorOpacity(tokens.color.onSurface, 0.6),
                marginBottom: utils.spacing.xs,
              }}>
                Built By
              </Text>
              <Text style={{
                fontSize: utils.typography.sizes.base,
                color: tokens.color.onSurface,
                fontWeight: utils.typography.weights.medium,
              }}>
                Pacaya Group
              </Text>
            </View>
          </View>
        </SectionCard>

        {/* Logout */}
        <Button
          title="Logout"
          onPress={handleLogout}
          variant="danger"
          style={{ marginTop: utils.spacing.md }}
        />
      </ScrollView>
    </PageContainer>
  );
}

